// src/api/billWarning.js
// 账单预警(逾期提醒) 接口层。新接口在此定义，既有接口从原位置 re-export 统一出口。
// 接口契约详见 docs/superpowers/specs/2026-07-14-overdue-reminder-design.md §8。
// 全局类型 APIResponse / ListResponse / PagedResponse 见 src/types/api.d.js。
import request from '@/utils/request'

// ---------------- 既有接口 re-export ----------------
// 车牌模糊搜索: /admin/v1/vehicle/car/vehicle/data
// 入参 {plateNo:string, state:1}；出参 [{id:number, plateNo:string}]
export { FuzzySearch } from '@/api/domain/cars'
// 司机姓名搜索(脱敏): 入参 {customerName:string, mixedType:'YES'}
// 出参 [{id, customerName, customerTel}]；下拉展示"姓名（手机号）"(参考个人租赁订单，同名司机靠手机号区分)
export { searchByCustomerNameSensitive } from '@/api/domain/customers'
// 车管/司管姓名搜索: 入参 {userName:string} 出参 [{id, userName, userMobile, deptName}]
export { getAllEmployeeList } from '@/api/carManage'
// 跟进历史 followList(入参 {bizId}) / 新增跟进 addFollow(入参 {bizId, describe})；bizId=风险ID或应收ID
export { followList, addFollow } from '@/api/domain/orders'

// ---------------- 类型定义 ----------------

/**
 * @typedef {Object} CustomerSearchItem 客户搜索结果项(身份证/司机姓名搜索)
 * @property {number} id - 客户ID(作为筛选 customerId 传参)
 * @property {string} customerName - 客户姓名
 * @property {string} customerTel - 客户电话(脱敏)
 */

/**
 * @typedef {Object} OverdueQuery 逾期列表/汇总/导出 的公共筛选参数(均可选)
 * @property {number} [carId] - 车辆ID(车牌搜索选中项)
 * @property {number} [customerId] - 客户ID(司机姓名或身份证搜索选中项)
 * @property {number} [vehicleManagerId] - 车管ID
 * @property {number} [driverManagerId] - 司管ID
 * @property {number} [rentStatusGroup] - 租赁状态分组(1-租赁中 / 2-已退车)
 * @property {number} [payMode] - 付款方式(10-日付 / 3-周付(满周) / 12-周付(自然周) / 4-月付(满月) / 5-月付(自然月))
 */

/**
 * @typedef {Object} OverdueOrderItem 逾期订单(车辆)列表项
 * @property {number} orderId - 订单ID
 * @property {string} plateNo - 车牌号
 * @property {string} customerName - 司机姓名
 * @property {string} customerTel - 司机电话(脱敏)
 * @property {string} idCardNo - 司机身份证(脱敏)【待后端在行内补充该字段】
 * @property {string} contractPeriod - 合同周期(整串)
 * @property {string} contractStartDate - 合同开始日期(yyyy-MM-dd)
 * @property {string} contractEndDate - 合同结束日期(yyyy-MM-dd)
 * @property {number} maxOverdueDays - 最大逾期天数
 * @property {string} overdueRentAmount - 逾期租金(元)
 * @property {string} overdueDepositAmount - 逾期押金(元)
 * @property {number} payMode - 付款方式code
 * @property {string} payModeName - 付款方式名称
 * @property {string} vehicleManagerName - 车管姓名
 * @property {string} driverManagerName - 司管姓名
 * @property {number} rentStatus - 租赁状态code
 * @property {string} rentStatusName - 租赁状态名称
 */

/**
 * @typedef {Object} OverdueOrderSummary 逾期订单汇总(列表页 4 个汇总卡片，按筛选条件聚合)
 * @property {string} totalReceivableAmount - 应收总额(元)
 * @property {string} totalReceivedAmount - 已收(实收)总额(元)
 * @property {string} totalOverdueAmount - 逾期总额(元)
 * @property {number} overdueCarCount - 逾期车辆数(未结清)
 */

/**
 * @typedef {Object} OverdueOrderBasicInfo 逾期订单详情-基本信息(车辆信息卡片)
 * @property {string} plateNo - 车牌号
 * @property {number} orderStatus - 订单状态code
 * @property {string} orderStatusName - 订单状态名称
 * @property {string} customerName - 司机姓名
 * @property {string} contractPeriod - 租赁周期(整串)
 * @property {string} contractStartDate - 合同开始日期(yyyy-MM-dd)
 * @property {string} contractEndDate - 合同结束日期(yyyy-MM-dd)
 */

/**
 * @typedef {Object} OverdueOrderDetailSummary 逾期订单详情-汇总信息(3 个统计卡片)
 * @property {string} totalPayableAmount - 应缴纳总额(元)
 * @property {string} overdueRentAmount - 逾期租金(元)
 * @property {string} overdueDepositAmount - 逾期押金(元)
 */

/**
 * @typedef {Object} OverdueReceivableItem 逾期订单详情-应收明细项(财务记录表行)
 * @property {number} receivableId - 应收ID
 * @property {number} billType - 账单类型(1-租金 / 2-押金)
 * @property {string} billTypeName - 账单类型名称
 * @property {number} periodNum - 期数
 * @property {string} plateNo - 车牌号
 * @property {string} billCycle - 账单周期
 * @property {string} receivableAmount - 应收金额(元)
 * @property {string} receivingAmount - 收款中金额(元)
 * @property {string} receivedAmount - 已收金额(元)
 * @property {string} surplusAmount - 欠款金额(元)
 * @property {number} overdueDays - 逾期天数
 * @property {number} status - 收款状态(0-未结清 / 1-已完成)
 * @property {string} statusName - 状态名称
 * @property {string} followState - 跟进状态编码
 * @property {string} followStateName - 跟进状态名称
 * @property {string} followCount - 跟进次数
 * @property {number} overdueId - 逾期ID(跟进 followList/addFollow 入参)
 */

// ---------------- 新增接口 ----------------

/**
 * 根据身份证号精确查询客户(脱敏) —— 列表页身份证远程搜索下拉数据源
 * @param {Object} data 参数
 * @param {string} data.idCardNo - 身份证号(搜索关键字)
 * @returns {ListResponse<CustomerSearchItem>} 匹配的客户列表
 */
export function searchByIdCard(data = {}) {
  return request({
    url: '/admin/v1/vehicle/customer/searchByIdCard/sensitive',
    method: 'POST',
    data,
  })
}

/**
 * 逾期订单(车辆)列表
 * @param {OverdueQuery & {pageNum:number, pageSize:number}} data 筛选 + 分页参数
 * @returns {PagedResponse<OverdueOrderItem>} 逾期车辆分页列表；
 *   其中 data.extInfo {string} 为数据最后更新时间(列表页"数据更新时间"文案取值)
 */
export function getOverdueOrderList(data = {}) {
  return request({
    url: '/admin/v1/vehicle/debt/overdue/orderList',
    method: 'POST',
    data,
  })
}

/**
 * 逾期订单汇总(列表页 4 个汇总卡片，后端按筛选条件聚合)
 * @param {OverdueQuery} data 筛选参数(与列表一致，无分页)
 * @returns {APIResponse<OverdueOrderSummary>} 汇总指标
 */
export function getOverdueOrderSummary(data = {}) {
  return request({
    url: '/admin/v1/vehicle/debt/overdue/orderSummary',
    method: 'POST',
    data,
  })
}

/**
 * 逾期订单详情-基本信息(车辆信息卡片)
 * @param {Object} data 参数
 * @param {number} data.orderId - 订单ID
 * @returns {APIResponse<OverdueOrderBasicInfo>} 车辆基本信息
 */
export function getOverdueOrderBasicInfo(data = {}) {
  return request({
    url: '/admin/v1/vehicle/debt/overdue/orderDetail/basicInfo',
    method: 'POST',
    data,
  })
}

/**
 * 逾期订单详情-应收明细列表(财务记录表数据源，每项含 overdueId 供跟进使用)
 * @param {Object} data 参数
 * @param {number} data.orderId - 订单ID
 * @param {number} data.pageNum - 页码
 * @param {number} data.pageSize - 每页条数
 * @returns {PagedResponse<OverdueReceivableItem>} 应收明细分页列表
 */
export function getOverdueReceivableList(data = {}) {
  return request({
    url: '/admin/v1/vehicle/debt/overdue/orderDetail/receivableList',
    method: 'POST',
    data,
  })
}

/**
 * 逾期订单详情-汇总信息(3 个统计卡片)
 * @param {Object} data 参数
 * @param {number} data.orderId - 订单ID
 * @returns {APIResponse<OverdueOrderDetailSummary>} 详情统计指标
 */
export function getOverdueOrderDetailSummary(data = {}) {
  return request({
    url: '/admin/v1/vehicle/debt/overdue/orderDetail/summary',
    method: 'POST',
    data,
  })
}
